import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import type { Integration } from '@sentry/core';
import {
  isExpectedLearnosityDomMissingEvent,
  isLearnosityNetworkErrorEvent,
  sanitizeLearnosityBreadcrumb,
  sanitizeLearnosityTransaction,
} from './utils/learnositySentry';

const replaysSessionSampleRateRaw = Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? '0');
const replaysSessionSampleRate = Number.isFinite(replaysSessionSampleRateRaw) ? replaysSessionSampleRateRaw : 0;
const tracesSampleRateRaw = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0');
const tracesSampleRate = Number.isFinite(tracesSampleRateRaw) ? tracesSampleRateRaw : 0;

/**
 * Reads ignore patterns from environment variables.
 * Example env:
 * VITE_SENTRY_IGNORE_PATTERNS='["MathJax","ResizeObserver loop limit exceeded"]'
 */
function getDynamicIgnorePatterns(): RegExp[] {
  const raw = import.meta.env.VITE_SENTRY_IGNORE_PATTERNS;

  if (raw === undefined) {
    return [/MathJax/i, /ResizeObserver loop limit exceeded/i];
  }

  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map(p => (typeof p === 'string' ? p.trim() : ''))
      .filter(p => p.length > 0)
      .map(p => new RegExp(p, 'i'));
  } catch {
    return [];
  }
}

// Built-in patterns we know are always noise
const staticIgnorePatterns: RegExp[] = [/Large Render Blocking Asset/i, /Unable to render latex/i];

const dynamicIgnorePatterns = getDynamicIgnorePatterns();

function shouldIgnoreByPatterns(text: string): boolean {
  const allPatterns = [...staticIgnorePatterns, ...dynamicIgnorePatterns];
  return allPatterns.some(re => re.test(text));
}

function isAllowedTransaction(pathName: string): boolean {
  return (
    pathName.startsWith('/skill/') ||
    pathName.startsWith('/assignment/') ||
    pathName.startsWith('/progress/') ||
    pathName === '/'
  );
}

function isGraphqlNetworkErrorEvent(event: ErrorEvent): boolean {
  return event.tags?.graphql_network_error === 'true';
}

function hasGraphqlException(event: ErrorEvent, type: string): boolean {
  return (
    event.tags?.source === 'graphql' && event.exception?.values?.some(exception => exception.type === type) === true
  );
}

function getGraphqlDiagnosticText(event: ErrorEvent): string {
  if (hasGraphqlException(event, 'GraphQLResponseError')) {
    const errorMessages = event.contexts?.graphql?.errorMessages;
    return typeof errorMessages === 'string' ? errorMessages : '';
  }

  if (hasGraphqlException(event, 'GraphQLHttpError')) {
    const statusText = event.contexts?.http?.statusText;
    return typeof statusText === 'string' ? statusText : '';
  }

  return '';
}

Sentry.init({
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: import.meta.env.VITE_RELEASE_VERSION,
  integrations: defaultIntegrations => [
    ...defaultIntegrations.map((integration): Integration => {
      if (integration.name !== 'Dedupe' || !integration.processEvent) return integration;
      const processEvent = integration.processEvent.bind(integration);

      return {
        ...integration,
        async processEvent(event, hint, client) {
          const correlationId = event.tags?.source === 'graphql' ? event.tags.correlation_id : undefined;
          const startupCycleId =
            event.tags?.learnosity_init_terminal === 'true' && event.tags?.learnosity_api === 'items'
              ? event.contexts?.learnosity?.startupCycleId
              : undefined;
          const identities = [correlationId, startupCycleId].filter(
            (value): value is string => typeof value === 'string' && value.length > 0,
          );
          // Compare request/cycle identity locally; keep server-side grouping unchanged.
          const comparisonEvent =
            identities.length > 0 ? { ...event, fingerprint: [...(event.fingerprint ?? []), ...identities] } : event;
          const result = await processEvent(comparisonEvent, hint, client);
          return result === null ? null : event;
        },
      };
    }),
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // User-action writes fail in high-volume tracking paths (Component Entered/Left fire on nearly
      // every navigation), so promoting those sessions to full replay recording is the same cost the
      // GraphQLResponseError exclusion already avoids.
      beforeErrorSampling: event =>
        !hasGraphqlException(event, 'GraphQLResponseError') && event.tags?.source !== 'user_action',
    }),
    Sentry.browserApiErrorsIntegration({ eventTarget: false }),
  ],

  // ===== Performance Tracing =====
  // Global default sample rate for transactions (0.0 to 1.0).
  // Defaults to 0 (disabled). Example: 0.25 for 25% sampling in production.
  tracesSampler: samplingContext => {
    const transactionName = samplingContext?.name?.toLowerCase() ?? '';
    const attributeName = samplingContext?.attributes?.transaction?.toString().toLowerCase() ?? '';

    // 100% sampling on routes that host the Learnosity player
    if (isAllowedTransaction(transactionName) || isAllowedTransaction(attributeName)) {
      return 1.0;
    }

    return tracesSampleRate;
  },

  tracePropagationTargets: [],

  // ===== Session Replay =====
  // Sample rate for session replays (0.0 to 1.0), configured via
  // VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE.
  replaysSessionSampleRate: replaysSessionSampleRate,

  // Always capture replay when an error occurs.
  replaysOnErrorSampleRate: 1.0,

  enableLogs: true,

  // Sentry is only enabled if explicitly set to "true"
  enabled: import.meta.env.VITE_SENTRY_ENABLED === 'true' || import.meta.env.VITE_SENTRY_ENABLED === 'enabled',

  /**
   * beforeSend() gives us full control to drop events before they reach Sentry.
   * This is where we filter noise such as network issues, vendor errors, or
   * performance warnings that we don't want in the dashboard.
   */
  beforeSend(event) {
    // Exhausted Items API startup is actionable even when its vendor message
    // matches a noise rule. Callers still select and sanitize telemetry fields.
    if (
      event.tags?.learnosity === 'true' &&
      event.tags?.learnosity_api === 'items' &&
      event.tags?.learnosity_init_terminal === 'true'
    )
      return event;
    const message = event.message || event.logentry?.message || event.exception?.values?.[0]?.value || '';

    // Ignore “Large Render Blocking Asset” warnings (pure noise)
    if (/Large Render Blocking Asset/i.test(message)) {
      return null;
    }

    // Ignore network errors when the user is offline (not our bug)
    const exc = event.exception?.values?.[0];
    const isNetworkErrorMessage =
      typeof exc?.value === 'string' && (exc.value.includes('Failed to fetch') || exc.value.includes('Network Error'));

    if (
      (isNetworkErrorMessage || isLearnosityNetworkErrorEvent(event) || isGraphqlNetworkErrorEvent(event)) &&
      typeof navigator !== 'undefined' &&
      navigator.onLine === false
    ) {
      return null;
    }

    // The structured 50001 event for the inline and print players is an
    // expected rendering condition and replaces the old raw-string rule.
    if (isExpectedLearnosityDomMissingEvent(event)) {
      return null;
    }

    // 3) Ignore third-party library noise (static + env-based patterns)
    const allTextToScan = [
      message,
      getGraphqlDiagnosticText(event),
      ...(event.exception?.values?.[0]?.stacktrace?.frames?.map(f => `${f.filename ?? ''} ${f.module ?? ''}`) ?? []),
    ].join('\n');

    if (shouldIgnoreByPatterns(allTextToScan)) {
      return null;
    }

    // Learnosity callers select their own telemetry fields. Preserve native
    // Sentry context here after the narrow event filters above.
    return event;
  },

  beforeBreadcrumb(breadcrumb, hint) {
    return sanitizeLearnosityBreadcrumb(breadcrumb, hint);
  },

  beforeSendTransaction(event) {
    const allTextToScan = [
      event.transaction ?? '',
      ...(event.spans ?? []).map(span => `${span.op ?? ''} ${span.description ?? ''}`),
    ].join('\n');

    if (shouldIgnoreByPatterns(allTextToScan)) {
      return null;
    }

    return sanitizeLearnosityTransaction(event);
  },
});
